import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Country } from "country-state-city";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useAlert } from "react-alert";
import * as yup from "yup";
import {
  CandidateDetails,
  useUpdateCandidateDetailsMutation,
} from "../../redux/Hire/jobRequestApi";
import FormikInput from "../../shared/components/FormikInput";
import { CloseIcon } from "../../shared/components/Icons";
import { userAccess } from "../../utils/CommonFunction";

type Data = {
  salaryIOTF: string | null;
  probationPeriod: string | null;
  noticePeriodPostProbation: string | null;
  tentativeDOJ: string | null;
  country: string | null;
  email: string;
  countryCode: string;
};

const validationSchema = yup.object({
  salaryIOTF: yup
    .number()
    .typeError("Should be a number")
    .required("Salary is required"),
  probationPeriod: yup.string().required("Probation period is required"),
  noticePeriodPostProbation: yup.string().required("Notice Period is required"),
  tentativeDOJ: yup.string().required("Date of joining"),
  countryCode: yup.string().required("Location is required"),
  email: yup.string().email().required("Email is required"),
});

const MONTH_OPTIONS = new Array(6).fill(0).map((_, index) => ({
  name: (index + 1).toString(),
  value: (index + 1).toString(),
}));

type Props = {
  text: string;
  positiveResponse: () => void;
  cancel: () => void;
  isLoading: boolean;
  showModal: boolean;
  btnText?: string;
  details?: CandidateDetails;
  editDetailsText?: string;
};

const CandidateMoveConfirmModalShortlist = ({
  positiveResponse,
  cancel,
  isLoading,
  showModal,
  btnText = "Move to next stage",
  details,
  editDetailsText = "You need to add details before sending candidate to next stage",
}: Props) => {
  const alert = useAlert();

  const [updateCandidate, updateCandidateMutating] =
    useUpdateCandidateDetailsMutation();
  const [initialValues, setInitialValues] = useState<Data>({
    salaryIOTF: details?.salaryIOTF || "",
    probationPeriod: details?.probationPeriod || "",
    noticePeriodPostProbation: details?.noticePeriodPostProbation || "",
    tentativeDOJ: details?.tentativeDOJ || null,
    country: details?.country || "",
    countryCode: details?.countryCode || "",
    email: details?.email || "",
  });

  useEffect(() => {
    if (details) {
      setInitialValues({
        salaryIOTF: details.salaryIOTF,
        probationPeriod: details.probationPeriod,
        noticePeriodPostProbation: details.noticePeriodPostProbation,
        tentativeDOJ: details.tentativeDOJ,
        country: details.country,
        email: details.email || "",
        countryCode: details.countryCode || "",
      });
    }
  }, [details]);

  const submitForm = useCallback(
    async (data: Data, formikProps: FormikHelpers<Data>) => {
      if (details) {
        try {
          await updateCandidate({
            ...details,
            ...data,
            id: details.id,
            updatedAt: new Date().toISOString(),
            country: Country.getCountryByCode(data.countryCode)?.name || "",
            createdByName: userAccess().firstName + " " + userAccess().lastName,
            createdByUId: userAccess().userId,
          }).unwrap();
          alert.success("Candidate updated successfully");
          positiveResponse();
        } catch (e) {
          alert.error("Error while updating candidate");
        } finally {
          cancel();
        }

        formikProps.resetForm();
      }
    },
    [alert, cancel, details, positiveResponse, updateCandidate],
  );

  return (
    <Dialog open={showModal} onClose={cancel} maxWidth="md">
      <DialogTitle>
        <Grid container direction={"column"}>
          <Grid container item justifyContent={"flex-end"}>
            <IconButton
              aria-label="close"
              onClick={cancel}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>

          <Grid container item justifyContent={"center"}>
            <Typography variant="h5">{editDetailsText}</Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 4,
          my: 2,
        }}
      >
        <Grid container direction={"column"} item py={1}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={submitForm}
          >
            {(formikProps) => (
              <Form
                style={{
                  width: "100%",
                }}
              >
                <Grid container spacing={2}>
                  <Grid container item xs={6}>
                    <FormikInput
                      label="Salary (In AUD)"
                      name="salaryIOTF"
                      formikProps={formikProps}
                      className="full-width"
                      options={undefined}
                      fullWidth
                    />
                  </Grid>
                  <Grid container item xs={6}>
                    <FormikInput
                      inputType="date-picker"
                      label="Tentative date of joining"
                      inputFormat="MM/DD/YYYY"
                      formikProps={formikProps}
                      name="tentativeDOJ"
                      className="full-width"
                      options={undefined}
                    />
                  </Grid>
                  <Grid container item xs={6}>
                    <FormikInput
                      label="Country"
                      name="countryCode"
                      className="common-select-dropdown full-width"
                      formikProps={formikProps}
                      options={Country.getAllCountries().map(
                        ({ name, isoCode }) => ({
                          name,
                          value: isoCode,
                        }),
                      )}
                      inputType="select"
                      fullWidth
                    />
                  </Grid>
                  <Grid container item xs={6}>
                    <FormikInput
                      label="Probation Period (in months)"
                      name="probationPeriod"
                      className="common-select-dropdown full-width"
                      formikProps={formikProps}
                      options={MONTH_OPTIONS}
                      inputType="select"
                      fullWidth
                    />
                  </Grid>
                  <Grid container item xs={6}>
                    <FormikInput
                      label="Notice Period (post probation)"
                      name="noticePeriodPostProbation"
                      className="common-select-dropdown full-width"
                      formikProps={formikProps}
                      options={MONTH_OPTIONS}
                      inputType="select"
                      fullWidth
                    />
                  </Grid>
                  <Grid container item xs={6}>
                    <FormikInput
                      label="Email*"
                      name="email"
                      formikProps={formikProps}
                      className="full-width"
                      options={undefined}
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    container
                    item
                    xs={12}
                    justifyContent={"flex-end"}
                    gap={1}
                  >
                    <Button onClick={cancel}>Cancel</Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={updateCandidateMutating.isLoading}
                    >
                      {updateCandidateMutating.isLoading ? (
                        <CircularProgress size={24}></CircularProgress>
                      ) : (
                        btnText
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateMoveConfirmModalShortlist;
